import { queryOptions } from '@tanstack/react-query'
import {
  buildScopedAdminQueryKey,
  type AdminQueryRoot,
  type OrganizationId,
} from '@/lib/organization-scoping'
import {
  getStatementCandidates,
  getStatementRun,
  getStatementRuns,
  previewStatementRun,
  type StatementCandidatesKeyEntries,
  type StatementRunKeyEntries,
  type StatementRunsKeyEntries,
} from './statements.api'
import type { StatementCandidateFilters } from './statements.schema'

interface StatementScope {
  organizationId: OrganizationId
}

/** The key entries each fetcher reads, under the shared statement-run root. */
type StatementQueryKey<TEntries> = [AdminQueryRoot & TEntries]

const root = { entity: 'customer-statement-runs' }

function candidatesQueryKey({
  organizationId,
  filters,
}: StatementCandidatesKeyEntries): StatementQueryKey<StatementCandidatesKeyEntries> {
  return buildScopedAdminQueryKey(root, { organizationId, scope: 'candidates', filters })
}
function runsQueryKey({
  organizationId,
  page,
}: StatementRunsKeyEntries): StatementQueryKey<StatementRunsKeyEntries> {
  return buildScopedAdminQueryKey(root, {
    organizationId,
    scope: 'runs',
    ...(page ? { page } : {}),
  })
}
function runQueryKey({
  organizationId,
  runId,
}: StatementRunKeyEntries): StatementQueryKey<StatementRunKeyEntries> {
  return buildScopedAdminQueryKey(root, { organizationId, scope: 'run', runId })
}

export const statementQueries = {
  preview: ({ organizationId, runId }: StatementScope & { runId: number }) =>
    queryOptions({
      queryKey: buildScopedAdminQueryKey(root, { organizationId, scope: 'preview', runId }),
      queryFn: () => previewStatementRun({ organizationId, runId }),
      staleTime: Infinity,
    }),
  candidates: ({
    organizationId,
    filters,
  }: StatementScope & { filters: StatementCandidateFilters }) =>
    queryOptions({
      queryKey: candidatesQueryKey({ organizationId, filters }),
      queryFn: getStatementCandidates,
    }),
  runs: ({ organizationId, page }: StatementScope & { page?: number }) =>
    queryOptions({
      queryKey: runsQueryKey({ organizationId, page }),
      queryFn: getStatementRuns,
      refetchInterval: (query) =>
        query.state.data?.data.some((run) => run.queued_count > 0 || run.processing_count > 0)
          ? 3000
          : false,
    }),
  run: ({ organizationId, runId }: StatementScope & { runId: number }) =>
    queryOptions({
      queryKey: runQueryKey({ organizationId, runId }),
      queryFn: getStatementRun,
      refetchInterval: (query) =>
        query.state.data?.items.some((item) => ['queued', 'processing'].includes(item.status))
          ? 3000
          : false,
    }),
}
